import React from "react";
import { useEffect } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { useToasts } from "react-toast-notifications";
import { Segment, Header, Divider, Button } from "semantic-ui-react";

import usePublicInspectionRequestMutation from "../../../hooks/inspections/public/usePublicInspectionRequestMutation";
import InspectionRequestDateTimeForm from "./InspectionRequestModalDateTimeForm";
import InspectionRequestHeader from "./InspectionRequestModalHeader";
import InspectionRequestOnSiteContactForm from "./InspectionRequestModalOnSiteContactForm";
import usePublicInspection from "../../../hooks/inspections/public/usePublicInspection";

export interface IInspectionRequestForm {
  onSiteContactName: string;
  onSiteContactPhone: string;
  onSiteContactInstructions: string;
  message: string | null;
  preferences: {
    date: Date | undefined;
    meridiem: "AM" | "PM" | null;
  }[];
}
export default function InspectionRequestModalContent({
  inspectionId,
  onClose,
}: {
  inspectionId: string;
  onClose: () => void;
}) {
  const { data } = usePublicInspection({ id: inspectionId });
  const { addToast } = useToasts();

  const onSuccess = () => {
    addToast("Inspection request made successfully", { appearance: "success", autoDismiss: true });
    handleClose();
  };

  const onError = () => {
    addToast("Inspection request failed", { appearance: "error", autoDismiss: true });
  };

  const { updateInspectionRequest, isLoading } = usePublicInspectionRequestMutation({
    id: inspectionId,
    onError,
    onSuccess,
  });

  const formContext = useForm<IInspectionRequestForm>({
    defaultValues: {
      onSiteContactName: "",
      onSiteContactPhone: "",
      onSiteContactInstructions: "",
      message: "",
      preferences: [],
    },
  });

  const handleClose = () => {
    formContext.reset();
    onClose();
  };

  const onSubmit: SubmitHandler<IInspectionRequestForm> = (data) => {
    updateInspectionRequest({
      data: {
        schedulingPreferencesMessage: data.message,
        schedulingPreferences: data.preferences.map((date) => {
          return {
            preferredDate: date.date?.toISOString() ?? "",
            meridiem: date.meridiem,
          };
        }),
        onSiteContactName: data.onSiteContactName,
        onSiteContactPhone: data.onSiteContactPhone,
        onSiteContactApplicantInstructions: data.onSiteContactInstructions,
      },
    });
  };

  useEffect(() => {
    if (data) {
      const attributes = data.data.attributes;
      const name = attributes.onSiteContactName ?? "";
      const phone = attributes.onSiteContactPhone ?? "";
      const instructions = attributes.onSiteContactApplicantInstructions ?? "";

      const request = attributes.request;

      const preferences = request.preferences;
      const message = request.message;

      formContext.reset({
        preferences: preferences.map((pref) => {
          return {
            date: !pref.date ? undefined : new Date(pref.date),
            meridiem: pref.meridiem,
          };
        }),
        message: message,
        onSiteContactName: name,
        onSiteContactPhone: phone,
        onSiteContactInstructions: instructions,
      });
    }
  }, [data]);

  return (
    <Segment className='tw-mt-0 tw-p-0 tw-text-base'>
      <div className='tw-p-4'>
        <Header as='h2'>Request Inspection</Header>
      </div>
      <Divider />
      <div className='tw-px-4'>
        <InspectionRequestHeader id={inspectionId} />
      </div>
      <Divider />
      <div className='tw-px-4'>
        <FormProvider {...formContext}>
          <InspectionRequestDateTimeForm id={inspectionId} errors={formContext.errors} />
        </FormProvider>
      </div>
      <Divider className='tw-mt-6' />
      <div className='tw-px-4'>
        <FormProvider {...formContext}>
          <InspectionRequestOnSiteContactForm
            name={"onSiteContactName"}
            phone={"onSiteContactPhone"}
            instructions='onSiteContactInstructions'
            errors={formContext.errors}
          />
        </FormProvider>
      </div>
      <Divider className='tw-mb-0' />
      <div className='tw-flex tw-justify-end tw-border-none tw-bg-[#F5F7F8] tw-p-3'>
        <Button basic onClick={handleClose}>
          Cancel
        </Button>
        <Button loading={isLoading} onClick={formContext.handleSubmit(onSubmit)} primary>
          Schedule
        </Button>
      </div>
    </Segment>
  );
}
